import { useState, useEffect } from "react";
import { useLocation, useRoute } from "wouter";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { useLocation as useGeoLocation } from "@/contexts/LocationContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from "@/components/ui/form";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { 
  insertMarketListingSchema, 
  marketCategories, 
  australianCities, 
  regions, 
  type InsertMarketListing,
  type MarketListing
} from "@shared/schema";
import { ArrowLeft, MapPin, DollarSign, Package, Navigation, ImageIcon } from "lucide-react";
import { z } from "zod";
import { MarketImageUploader } from "@/components/MarketImageUploader";
import { SignInRequired } from "@/components/SignInRequired";
import { inferZoneIdFromLocation } from "@shared/zones";

const CREATE_POST_DRAFT_KEY = "ozk_local_create_post_draft_v1";

const formSchema = z.object({
  userId: z.string(),
  userEmail: z.string(),
  userDisplayName: z.string(),
  userPhotoUrl: z.string().optional(),
  title: z.string().min(5, "제목은 최소 5자 이상이어야 합니다"),
  description: z.string().min(20, "설명은 최소 20자 이상이어야 합니다"),
  priceCents: z.number().int(),
  category: z.string().min(1, "카테고리를 선택해주세요"),
  condition: z.string(),
  state: z.string().min(1, "주/지역을 선택해주세요"),
  city: z.string().min(1, "도시를 선택해주세요"),
  suburb: z.string().optional(),
  postcode: z.string().optional(),
  latitude: z.number().optional(),
  longitude: z.number().optional(),
  images: z.array(z.string()).default([]),
  contactMethod: z.string().min(1, "연락 방법을 선택해주세요"),
  phoneNumber: z.string().optional(),
  deliveryAvailable: z.boolean().default(false),
  pickupLocations: z.string().optional(),
  status: z.string(),
  isPremium: z.boolean().default(false),
  premiumUntil: z.date().optional(),
  views: z.number().optional(),
  favorites: z.number().optional(),
  expiresAt: z.union([z.date(), z.string()]),
});

const itemConditions = [
  { id: "new", label: "새상품 (미개봉)" },
  { id: "like-new", label: "거의 새것" },
  { id: "good", label: "좋음" },
  { id: "fair", label: "보통" },
  { id: "poor", label: "나쁨" },
];

export default function MarketRegister() {
  const { user } = useAuth();
  const [, setLocation] = useLocation();
  const { userLocation, requestLocation } = useGeoLocation();
  const { toast } = useToast();
  const [selectedState, setSelectedState] = useState<string>("");
  const [priceType, setPriceType] = useState<"paid" | "free" | "negotiable">("paid");
  const [priceDisplay, setPriceDisplay] = useState<string>("");
  const [initialImages, setInitialImages] = useState<string[]>([]);

  // Check if we're in edit mode
  const [match, params] = useRoute<{ id: string }>("/market/edit/:id");
  const listingId = match && params ? params.id : null;

  // Fetch existing listing if in edit mode
  // NOTE: a custom queryFn is required here - the default queryFn only fetches
  // queryKey[0] ("/api/market", the list endpoint), ignoring listingId, which
  // silently broke edit-mode prefill. See MarketDetail.tsx for the same fix.
  const { data: existingListing, isLoading: loadingListing } = useQuery<MarketListing>({
    queryKey: ['/api/market', listingId],
    queryFn: async () => {
      const res = await fetch(`/api/market/${listingId}`);
      if (!res.ok) throw new Error("Failed to fetch listing");
      const json = await res.json();
      return json.data || json;
    },
    enabled: !!listingId,
  });

  // Calculate expiry date (7 days from now)
  const getExpiryDate = () => {
    const date = new Date();
    date.setDate(date.getDate() + 7);
    return date;
  };

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      userId: user?.uid || "",
      userEmail: user?.email || "",
      userDisplayName: user?.displayName || "",
      userPhotoUrl: user?.photoURL || undefined,
      title: "",
      description: "",
      priceCents: 0,
      category: "",
      condition: "good",
      state: "",
      city: "",
      suburb: "",
      postcode: "",
      latitude: undefined,
      longitude: undefined,
      images: [],
      contactMethod: "phone",
      phoneNumber: "",
      deliveryAvailable: false,
      pickupLocations: "",
      status: "available",
      isPremium: false,
      premiumUntil: undefined,
      views: 0,
      favorites: 0,
      expiresAt: getExpiryDate().toISOString(), // ISO 문자열로 기본값 설정
    },
  });

  // Populate form with existing listing data
  useEffect(() => {
    if (existingListing && listingId) {
      form.reset({
        userId: existingListing.userId,
        userEmail: existingListing.userEmail,
        userDisplayName: existingListing.userDisplayName,
        userPhotoUrl: existingListing.userPhotoUrl || undefined,
        title: existingListing.title,
        description: existingListing.description,
        priceCents: existingListing.priceCents,
        category: existingListing.category,
        condition: existingListing.condition ?? "",
        state: existingListing.state,
        city: existingListing.city,
        suburb: existingListing.suburb ?? "",
        postcode: existingListing.postcode ?? "",
        latitude: existingListing.latitude !== null ? parseFloat(String(existingListing.latitude)) : undefined,
        longitude: existingListing.longitude !== null ? parseFloat(String(existingListing.longitude)) : undefined,
        images: existingListing.images || [],
        contactMethod: existingListing.contactMethod,
        phoneNumber: existingListing.phoneNumber ?? "",
        deliveryAvailable: existingListing.deliveryAvailable ?? false,
        pickupLocations: existingListing.pickupLocations ?? "",
        status: existingListing.status,
        isPremium: existingListing.isPremium ?? false,
        premiumUntil: existingListing.premiumUntil ?? undefined,
        views: existingListing.views ?? 0,
        favorites: existingListing.favorites ?? 0,
        expiresAt: existingListing.expiresAt,
      });

      // Set state for selects
      setSelectedState(existingListing.state);
      
      // Set price type and display
      if (existingListing.priceCents === 0) {
        setPriceType("free");
        setPriceDisplay("0");
      } else if (existingListing.priceCents === -1) {
        setPriceType("negotiable");
        setPriceDisplay("");
      } else {
        setPriceType("paid");
        setPriceDisplay((existingListing.priceCents / 100).toFixed(0));
      }

      // Set initial images
      setInitialImages(existingListing.images || []);
    }
  }, [existingListing, listingId, form]);

  useEffect(() => {
    if (listingId) return;

    try {
      const rawDraft = sessionStorage.getItem(CREATE_POST_DRAFT_KEY);
      if (!rawDraft) return;

      const draft = JSON.parse(rawDraft);
      if (draft?.type !== "market") return;

      const draftState = typeof draft.state === "string" ? draft.state : "";
      const draftSuburb = typeof draft.suburb === "string" ? draft.suburb : "";
      form.setValue("title", typeof draft.title === "string" ? draft.title : "");
      form.setValue(
        "description",
        typeof draft.description === "string" ? draft.description : "",
      );
      form.setValue("state", draftState);
      form.setValue("city", draftSuburb || draftState);
      form.setValue("suburb", draftSuburb);
      setSelectedState(draftState);
      sessionStorage.removeItem(CREATE_POST_DRAFT_KEY);
    } catch (error) {
      console.warn("[MarketRegister] Failed to load create draft", error);
    }
  }, [form, listingId]);

  // Auto-fill GPS coordinates when user location is available (only for new listings)
  useEffect(() => {
    if (!listingId && userLocation?.latitude && userLocation?.longitude) {
      form.setValue("latitude", parseFloat(userLocation.latitude.toString()));
      form.setValue("longitude", parseFloat(userLocation.longitude.toString()));
    }
  }, [userLocation, form, listingId]);

  const createMutation = useMutation({
    mutationFn: async (data: InsertMarketListing) => {
      return await apiRequest("POST", "/api/market", data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/market"] });
      toast({
        title: "상품 등록 완료",
        description: "중고마켓에 상품이 성공적으로 등록되었습니다.",
      });
      setLocation("/");
    },
    onError: (error: Error) => {
      toast({
        title: "등록 실패",
        description: error.message || "상품 등록 중 오류가 발생했습니다.",
        variant: "destructive",
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: InsertMarketListing) => {
      return await apiRequest("PATCH", `/api/market/${listingId}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/market"] });
      queryClient.invalidateQueries({ queryKey: ["/api/market", listingId] });
      toast({
        title: "수정 완료",
        description: "중고마켓 상품이 성공적으로 수정되었습니다.",
      });
      setLocation("/");
    },
    onError: (error: Error) => {
      toast({
        title: "수정 실패",
        description: error.message || "상품 수정 중 오류가 발생했습니다.",
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: z.infer<typeof formSchema>) => {
    if (!user) {
      toast({
        title: "로그인 필요",
        description: "상품을 등록하려면 로그인이 필요합니다.",
        variant: "destructive",
      });
      return;
    }

    const zoneId = inferZoneIdFromLocation({
      suburb: data.suburb,
      city: data.city,
      state: data.state,
    });

    const submitData = {
      ...data,
      userId: user.uid,
      userEmail: user.email || "",
      userDisplayName: user.displayName || "",
      userPhotoUrl: user.photoURL || undefined,
      zone_id: zoneId,
      zoneId,
      expiresAt: data.expiresAt, // form data의 expiresAt을 그대로 사용 (이미 string)
    } as InsertMarketListing;

    if (listingId) {
      updateMutation.mutate(submitData);
    } else {
      createMutation.mutate(submitData);
    }
  };

  const handlePriceChange = (value: string) => {
    setPriceDisplay(value);
    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue >= 0) {
      form.setValue("priceCents", Math.round(numValue * 100));
    }
  };

  const handlePriceTypeChange = (type: "paid" | "free" | "negotiable") => {
    setPriceType(type);
    if (type === "free") {
      form.setValue("priceCents", 0);
      setPriceDisplay("0");
    } else if (type === "negotiable") {
      form.setValue("priceCents", -1);
      setPriceDisplay("");
    } else {
      setPriceDisplay("");
    }
  };

  const availableCities = selectedState && selectedState in australianCities
    ? australianCities[selectedState as keyof typeof australianCities]
    : [];

  if (!user) {
    return (
      <SignInRequired
        title="중고 물품 등록"
        description="거래 연락과 작성자 확인을 위해 로그인해주세요. 로그인 후 상품 상태와 가격 입력을 바로 계속할 수 있습니다."
        backHref="/market"
        backLabel="동네마켓 목록"
      />
    );
  }

  if (loadingListing && listingId) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">로딩 중...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-primary/5 border-b">
        <div className="container mx-auto px-4 py-6">
          <div className="max-w-3xl mx-auto">
            <Button
              variant="ghost"
              onClick={() => setLocation("/")}
              className="mb-4"
              data-testid="button-back"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              목록으로
            </Button>
            <h1 className="text-3xl font-bold flex items-center gap-2" data-testid="text-title">
              <span className="text-3xl">🥕</span>
              {listingId ? "중고거래 수정" : "중고거래 등록"}
            </h1>
            <p className="text-muted-foreground mt-2">
              우리 동네에서 안전하고 편리한 거래를 시작하세요
            </p>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-3xl mx-auto">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              {/* Basic Info Card */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Package className="w-5 h-5" />
                    상품 정보
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Title */}
                  <FormField
                    control={form.control}
                    name="title"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>상품명 *</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="예) 아이폰 13 Pro 256GB 실버 (구매일 2022.01)"
                            {...field}
                            data-testid="input-title"
                          />
                        </FormControl>
                        <FormDescription className="text-xs">
                          구매시기, 브랜드, 사용감 등 핵심 정보를 포함하면 좋아요
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Category */}
                  <FormField
                    control={form.control}
                    name="category"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>카테고리 *</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger data-testid="select-category">
                              <SelectValue placeholder="카테고리 선택" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {marketCategories.map((category) => (
                              <SelectItem key={category.id} value={category.id}>
                                {category.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Description */}
                  <FormField
                    control={form.control}
                    name="description"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>상세 설명 *</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="상품 상태, 구매 시기, 사용 빈도 등을 자세히 적어주세요&#10;&#10;예)&#10;- 2022년 1월 구매&#10;- 케이스 항상 착용하여 사용&#10;- 스크래치 없이 깨끗합니다&#10;- 배터리 효율 90% 이상&#10;- 박스, 충전기, 이어폰 모두 포함"
                            className="min-h-[150px]"
                            {...field}
                            data-testid="input-description"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Condition */}
                  <FormField
                    control={form.control}
                    name="condition"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>상품 상태 *</FormLabel>
                        <FormControl>
                          <RadioGroup
                            onValueChange={field.onChange}
                            value={field.value || undefined}
                            className="grid grid-cols-2 gap-3"
                          >
                            {itemConditions.map((condition) => (
                              <div key={condition.id} className="flex items-center space-x-2">
                                <RadioGroupItem value={condition.id} id={condition.id} data-testid={`radio-condition-${condition.id}`} />
                                <label
                                  htmlFor={condition.id}
                                  className="text-sm font-normal cursor-pointer"
                                >
                                  {condition.label}
                                </label>
                              </div>
                            ))}
                          </RadioGroup>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>

              {/* Image Upload Card */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <ImageIcon className="w-5 h-5" />
                    상품 사진
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <MarketImageUploader
                    maxImages={10}
                    onImagesChange={(paths) => form.setValue("images", paths)}
                    userId={user?.uid || undefined}
                    initialImages={initialImages}
                  />
                  <FormDescription className="mt-4 text-xs">
                    상품과 관련된 사진을 최대 10장까지 등록할 수 있어요.
                    <br />
                    첫 번째 사진이 대표 이미지로 보여져요.
                  </FormDescription>
                </CardContent>
              </Card>

              {/* Price Card */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <DollarSign className="w-5 h-5" />
                    판매가
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Price Type */}
                  <div className="space-y-3">
                    <label className="text-sm font-medium">가격 유형 *</label>
                    <RadioGroup
                      value={priceType}
                      onValueChange={(value) => handlePriceTypeChange(value as "paid" | "free" | "negotiable")}
                      className="flex gap-4"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="paid" id="paid" data-testid="radio-price-paid" />
                        <label htmlFor="paid" className="text-sm font-normal cursor-pointer">
                          판매
                        </label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="free" id="free" data-testid="radio-price-free" />
                        <label htmlFor="free" className="text-sm font-normal cursor-pointer">
                          무료나눔
                        </label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="negotiable" id="negotiable" data-testid="radio-price-negotiable" />
                        <label htmlFor="negotiable" className="text-sm font-normal cursor-pointer">
                          가격협의
                        </label>
                      </div>
                    </RadioGroup>
                  </div>

                  {/* Price Input (only for paid type) */}
                  {priceType === "paid" && (
                    <div>
                      <label className="text-sm font-medium mb-2 block">판매 가격 *</label>
                      <div className="flex items-center gap-2">
                        <span className="text-lg">$</span>
                        <Input
                          type="number"
                          placeholder="0"
                          value={priceDisplay}
                          onChange={(e) => handlePriceChange(e.target.value)}
                          min="0"
                          step="1"
                          className="flex-1"
                          data-testid="input-price"
                        />
                        <span className="text-sm text-muted-foreground">AUD</span>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Location Card */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MapPin className="w-5 h-5" />
                    거래 위치
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* State */}
                  <FormField
                    control={form.control}
                    name="state"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>주/지역 *</FormLabel>
                        <Select
                          onValueChange={(value) => {
                            field.onChange(value);
                            setSelectedState(value);
                            form.setValue("city", "");
                          }}
                          value={field.value}
                        >
                          <FormControl>
                            <SelectTrigger data-testid="select-state">
                              <SelectValue placeholder="주 선택" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {regions.filter(r => r.id !== "ALL").map((region) => (
                              <SelectItem key={region.id} value={region.id}>
                                {region.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* City */}
                  {availableCities.length > 0 && (
                    <FormField
                      control={form.control}
                      name="city"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>도시 *</FormLabel>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl>
                              <SelectTrigger data-testid="select-city">
                                <SelectValue placeholder="도시 선택" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {availableCities.map((city) => (
                                <SelectItem key={city.id} value={city.id}>
                                  {city.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}

                  {/* Suburb */}
                  <FormField
                    control={form.control}
                    name="suburb"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>서버브 (선택)</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="예: Chatswood, Eastwood 등"
                            {...field}
                            value={field.value || ""}
                            data-testid="input-suburb"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Postcode */}
                  <FormField
                    control={form.control}
                    name="postcode"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>우편번호 (선택)</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="예: 2067"
                            {...field}
                            value={field.value || ""}
                            data-testid="input-postcode"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* GPS Location */}
                  <div className="p-4 bg-muted/50 rounded-md">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <h4 className="font-medium mb-1 flex items-center gap-2">
                          <Navigation className="w-4 h-4" />
                          내 위치 설정
                        </h4>
                        <p className="text-sm text-muted-foreground">
                          {userLocation?.latitude && userLocation?.longitude
                            ? "위치가 설정되었습니다. 근처 사용자들이 거래글을 더 쉽게 찾을 수 있어요."
                            : "위치를 설정하면 근처 사용자들이 거래글을 더 쉽게 찾을 수 있어요."}
                        </p>
                      </div>
                      <Button
                        type="button"
                        variant={userLocation?.latitude ? "secondary" : "outline"}
                        size="sm"
                        onClick={requestLocation}
                        disabled={!!userLocation?.latitude}
                        data-testid="button-request-location"
                      >
                        {userLocation?.latitude ? "설정됨" : "위치 설정"}
                      </Button>
                    </div>
                  </div>

                  {/* Pickup Locations */}
                  <FormField
                    control={form.control}
                    name="pickupLocations"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>거래 가능 장소 (선택)</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="예: Chatswood역, Eastwood 도서관 앞"
                            {...field}
                            value={field.value || ""}
                            data-testid="input-pickup-locations"
                          />
                        </FormControl>
                        <FormDescription className="text-xs">
                          만날 수 있는 장소를 구체적으로 적어주세요
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Delivery Available */}
                  <FormField
                    control={form.control}
                    name="deliveryAvailable"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                        <FormControl>
                          <Checkbox
                            checked={field.value || false}
                            onCheckedChange={field.onChange}
                            data-testid="checkbox-delivery"
                          />
                        </FormControl>
                        <div className="space-y-1 leading-none">
                          <FormLabel className="font-normal">
                            택배거래 가능
                          </FormLabel>
                          <FormDescription className="text-xs">
                            택배로 보내드릴 수 있어요 (택배비 별도)
                          </FormDescription>
                        </div>
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>

              {/* Contact Card */}
              <Card>
                <CardHeader>
                  <CardTitle>연락 방법</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Contact Method */}
                  <FormField
                    control={form.control}
                    name="contactMethod"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>선호 연락 방법 *</FormLabel>
                        <FormControl>
                          <RadioGroup
                            onValueChange={field.onChange}
                            value={field.value}
                            className="flex gap-4"
                          >
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem value="phone" id="phone" data-testid="radio-contact-phone" />
                              <label htmlFor="phone" className="text-sm font-normal cursor-pointer">
                                전화
                              </label>
                            </div>
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem value="both" id="both" data-testid="radio-contact-both" />
                              <label htmlFor="both" className="text-sm font-normal cursor-pointer">
                                전화+이메일
                              </label>
                            </div>
                          </RadioGroup>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Phone Number */}
                  {(form.watch("contactMethod") === "phone" || form.watch("contactMethod") === "both") && (
                    <FormField
                      control={form.control}
                      name="phoneNumber"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>전화번호 *</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="예: 0412 345 678"
                              {...field}
                              value={field.value || ""}
                              data-testid="input-phone"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}
                </CardContent>
              </Card>

              {/* Submit Button */}
              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setLocation("/")}
                  className="flex-1"
                  data-testid="button-cancel"
                >
                  취소
                </Button>
                <Button
                  type="submit"
                  className="flex-1"
                  disabled={createMutation.isPending || updateMutation.isPending}
                  data-testid="button-submit"
                >
                  {createMutation.isPending || updateMutation.isPending
                    ? "처리 중..."
                    : listingId
                    ? "수정 완료"
                    : "등록하기"}
                </Button>
              </div>
            </form>
          </Form>
        </div>
      </div>
    </div>
  );
}
